import { useQuery } from "@apollo/client";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useTenant } from "hooks/useTenant";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { TrayFilter } from "./NotificationTrayHeader";

import {
  GET_MY_NOTIFICATIONS,
  GET_MY_UNREAD_NOTIFICATION_COUNT,
} from "../../hooks/useNotifications";
import { useOakStream } from "../../stream";
import { NotificationBellButton } from "./NotificationBellButton";
import {
  useBellBounce,
  useNotificationReceiptActions,
  useNotificationToasts,
  useRowActionsMenu,
  useUnreadCount,
} from "./notificationBellHooks";
import {
  getNotificationDetailUrl,
  NOTIFICATIONS_LIST_URL,
  notificationSettingsUrl,
  type NotificationOpenTarget,
} from "./notificationNavigation";
import { burstSettleDelay, sortArrivals, takeArrivals } from "./notificationStream";
import { NotificationTray } from "./NotificationTray";
import { NotificationRowActionsMenu, NotificationTrayActionsMenu } from "./NotificationTrayMenus";
import { useNotificationsEnabled } from "./useNotificationsEnabled";

export function NotificationBell() {
  const isNotificationsEnabled = useNotificationsEnabled();
  const { tenantId } = useTenant();
  const { isStaff } = useCurrentUser();
  const settingsUrl = notificationSettingsUrl({ tenantId, isStaff });
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [topActionsAnchorEl, setTopActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [trayFilter, setTrayFilter] = useState<TrayFilter>("all");
  const [isBellBouncing, bounceBell] = useBellBounce();
  const burstTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const burstCreatedIdsRef = useRef<string[]>([]);
  const burstStartedAtRef = useRef<number | null>(null);
  const burstChangedOthersRef = useRef(false);
  // Runs when a burst settles; reassigned each render so it reads the current queries.
  const settleBurstRef = useRef<() => void>(() => {});
  const openNotificationRef = useRef<(target: NotificationOpenTarget) => void>(() => {});
  const openInboxRef = useRef<() => void>(() => {});
  const open = Boolean(anchorEl);

  const countQuery = useQuery(GET_MY_UNREAD_NOTIFICATION_COUNT, {
    skip: !isNotificationsEnabled,
    fetchPolicy: "cache-and-network",
  });
  const listQuery = useQuery(GET_MY_NOTIFICATIONS, {
    variables: {
      page: 0,
      pageSize: 8,
      includeArchived: false,
      onlyUnread: trayFilter === "unread",
    },
    skip: !isNotificationsEnabled || !open,
    fetchPolicy: "cache-and-network",
  });
  const stream = useOakStream({
    url: "/events/notifications",
    enabled: isNotificationsEnabled,
  });
  const handledStreamEventIdRef = useRef<string | null | undefined>(undefined);
  const { showNotificationToast, showBurstToast } = useNotificationToasts(
    openNotificationRef,
    openInboxRef
  );
  const unread = useUnreadCount(
    countQuery.data?.myUnreadNotificationCount ?? 0,
    countQuery.refetch
  );

  const notifications = useMemo(
    () =>
      (listQuery.data?.myNotifications.items ?? []).filter(
        (notificationUser) => !notificationUser.archivedAt
      ),
    [listQuery.data?.myNotifications.items]
  );
  const rowActions = useRowActionsMenu(notifications);

  const handleClose = () => {
    setAnchorEl(null);
    setTopActionsAnchorEl(null);
    rowActions.close();
  };

  const actions = useNotificationReceiptActions({
    unread,
    refreshCount: countQuery.refetch,
    refreshList: () => {
      if (open) {
        void listQuery.refetch();
      }
    },
    close: handleClose,
  });
  const { consumeOwnEcho } = actions;
  openNotificationRef.current = actions.open;
  const closeAndNavigate = (url: string) => {
    handleClose();
    navigate(url);
  };
  const openInbox = () => closeAndNavigate(NOTIFICATIONS_LIST_URL);
  openInboxRef.current = openInbox;

  settleBurstRef.current = () => {
    const createdIds = burstCreatedIdsRef.current;
    const changedOthers = burstChangedOthersRef.current;
    burstCreatedIdsRef.current = [];
    burstChangedOthersRef.current = false;
    burstStartedAtRef.current = null;
    if (changedOthers) {
      unread.set(null);
    }
    void countQuery.refetch();
    if (open && changedOthers) {
      void listQuery.refetch();
    }
    const [onlyCreatedId] = createdIds;
    if (!onlyCreatedId) {
      return;
    }
    bounceBell();
    if (createdIds.length === 1) {
      void showNotificationToast(onlyCreatedId);
    } else {
      showBurstToast(createdIds.length);
    }
  };

  useEffect(() => {
    return () => {
      if (burstTimeoutRef.current) {
        clearTimeout(burstTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const arrived = takeArrivals(stream, handledStreamEventIdRef);
    if (arrived.length === 0) {
      return;
    }

    const { createdIds, changedElsewhere } = sortArrivals(arrived, consumeOwnEcho);
    burstCreatedIdsRef.current.push(...createdIds);
    burstChangedOthersRef.current ||= changedElsewhere;

    if (burstTimeoutRef.current) {
      clearTimeout(burstTimeoutRef.current);
    }
    const startedAt = burstStartedAtRef.current ?? Date.now();
    burstStartedAtRef.current = startedAt;
    burstTimeoutRef.current = setTimeout(
      () => {
        burstTimeoutRef.current = null;
        settleBurstRef.current();
      },
      burstSettleDelay(startedAt, Date.now())
    );
  }, [stream, consumeOwnEcho]);

  if (!isNotificationsEnabled) {
    return null;
  }

  return (
    <>
      <NotificationBellButton
        open={open}
        hasUnread={unread.unreadCount > 0}
        bouncing={isBellBouncing}
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
          void countQuery.refetch();
        }}
      />
      <NotificationTray
        anchorEl={anchorEl}
        onClose={handleClose}
        filter={trayFilter}
        onFilterChange={setTrayFilter}
        notifications={notifications}
        loading={listQuery.loading}
        rowActionsNotificationUserId={rowActions.rowId}
        onOpenNotification={actions.open}
        onRowActionsClick={rowActions.open}
        onTopActionsClick={(event) => setTopActionsAnchorEl(event.currentTarget)}
        onShowAll={openInbox}
        onSettings={settingsUrl ? () => closeAndNavigate(settingsUrl) : null}
      />
      <NotificationTrayActionsMenu
        anchorEl={topActionsAnchorEl}
        onClose={() => setTopActionsAnchorEl(null)}
        canMarkAllRead={unread.unreadCount > 0 && !actions.markingAllRead}
        canMarkAllUnread={notifications.length > 0 && !actions.markingAllUnread}
        onMarkAllRead={actions.markAllRead}
        onMarkAllUnread={actions.markAllUnread}
      />
      <NotificationRowActionsMenu
        anchorEl={rowActions.anchorEl}
        onClose={rowActions.close}
        notificationUser={rowActions.row}
        updating={actions.updatingNotificationId === rowActions.rowId}
        // The tray's way to the notification itself rather than the record it is
        // about, for the reader whose question is "what did this say?". Leaves the
        // receipt alone — the detail page marks it read on arrival.
        onView={() =>
          rowActions.withRow((target) => {
            handleClose();
            navigate(getNotificationDetailUrl(target.id));
          })
        }
        onToggleRead={() => rowActions.withRow(actions.toggleRead)}
        onArchive={() => rowActions.withRow(actions.archive)}
      />
    </>
  );
}
